import type { BuildingStore } from '../buildings/BuildingStore';
import type { PlayerId } from '../ids';
import { isPlayerId } from '../ids';
import type { MatchPlayer } from '../match';
import type { UnitStore } from '../units/UnitStore';
import type { ModeConfig } from './modes';

export type PlayerStatus = 'playing' | 'defeated';

export interface MatchResult {
  /** Winning team, or null for a draw / the defence holding with no winner. */
  readonly winnerTeam: number | null;
  readonly winners: readonly PlayerId[];
  readonly reason: string;
  /** Simulation seconds when it ended. */
  readonly at: number;
}

export interface PlayerStats {
  readonly unitsLost: number;
  readonly kills: number;
  readonly buildingsLost: number;
}

export type MatchEvent =
  | { type: 'defeated'; playerId: PlayerId; reason: string }
  | { type: 'ended'; result: MatchResult };

/**
 * Victory / defeat for a mode (Phase 9, DECISIONS D83). Engine-free; listens
 * to the stores, never mutates gameplay state.
 */
export class MatchRules {
  private time = 0;
  private readonly status = new Map<PlayerId, PlayerStatus>();
  private readonly stats = new Map<PlayerId, { unitsLost: number; kills: number; buildingsLost: number }>();
  private ended: MatchResult | null = null;
  private readonly listeners = new Set<(e: MatchEvent) => void>();
  private readonly unsubs: (() => void)[] = [];

  constructor(
    private readonly mode: ModeConfig,
    private readonly players: readonly MatchPlayer[],
    private readonly buildings: BuildingStore,
    units: UnitStore,
    /** Hordes sent so far (survive goal). */
    private readonly hordes: { readonly wavesSent: number } = { wavesSent: 0 },
    /** D124 objectives (nests left, camps reached). */
    private readonly goals: { readonly nestsLeft: number; readonly rescued: number } = { nestsLeft: 0, rescued: 0 },
  ) {
    for (const p of players) {
      this.status.set(p.id, 'playing');
      this.stats.set(p.id, { unitsLost: 0, kills: 0, buildingsLost: 0 });
    }
    this.unsubs.push(
      buildings.subscribe((e) => {
        if (e.type !== 'removed' || !e.destroyed) return;
        const o = e.building.ownerId;
        if (isPlayerId(o)) this.stats.get(o) && this.stats.get(o)!.buildingsLost++;
        if (e.building.type === 'command_center' && isPlayerId(o)) this.check();
      }),
      units.subscribe((e) => {
        if (e.type !== 'died') return;
        const o = e.unit.ownerId;
        if (isPlayerId(o)) this.stats.get(o) && this.stats.get(o)!.unitsLost++;
        if (e.by && e.by > 0) {
          const killer = units.get(e.by)?.ownerId;
          if (killer && isPlayerId(killer)) this.stats.get(killer) && this.stats.get(killer)!.kills++;
        }
      }),
    );
  }

  subscribe(l: (e: MatchEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.listeners.clear();
  }

  get result(): MatchResult | null {
    return this.ended;
  }

  get elapsed(): number {
    return this.time;
  }

  /** Seconds left on a timed mode, or null. */
  get timeLeft(): number | null {
    return this.mode.victory.kind === 'raid' ? Math.max(0, this.mode.victory.seconds - this.time) : null;
  }

  /** Survive goal: seconds still to hold after the last horde came, or null before it (or in other modes). */
  get holdLeft(): number | null {
    const v = this.mode.victory;
    if (v.kind !== 'survive' || this.lastHordeAt === null) return null;
    return Math.max(0, this.lastHordeAt + v.holdSeconds - this.time);
  }

  private lastHordeAt: number | null = null;

  statusOf(p: PlayerId): PlayerStatus {
    return this.status.get(p) ?? 'defeated';
  }

  statsOf(p: PlayerId): PlayerStats {
    return { ...(this.stats.get(p) ?? { unitsLost: 0, kills: 0, buildingsLost: 0 }) };
  }

  /** A player gives up (Phase 10): defeated now, then the usual victory check. */
  forfeit(p: PlayerId, reason: string): void {
    if (this.ended || this.status.get(p) !== 'playing') return;
    this.status.set(p, 'defeated');
    this.emit({ type: 'defeated', playerId: p, reason });
    this.check();
  }

  step(dt: number): void {
    if (this.ended || !(dt > 0)) return;
    this.time += dt;
    if (this.mode.victory.kind === 'raid' && this.time >= this.mode.victory.seconds - 1e-9) {
      const defender = this.players[1];
      this.end({ winnerTeam: defender ? defender.team : null, winners: defender ? [defender.id] : [], reason: 'Time is up — the defence held.', at: this.time });
    }
    const v = this.mode.victory;
    if (v.kind === 'nests' || v.kind === 'rescue') {
      const done = v.kind === 'nests' ? this.goals.nestsLeft === 0 : this.goals.rescued >= v.count;
      const alive = this.players.filter((p) => this.status.get(p.id) === 'playing');
      if (done && alive.length) this.end({ winnerTeam: alive[0]!.team, winners: alive.map((p) => p.id), reason: v.kind === 'nests' ? 'Every nest is burned out.' : 'Every survivor is home.', at: this.time });
    }
    if (v.kind === 'survive') {
      if (this.lastHordeAt === null && this.hordes.wavesSent >= v.waves) this.lastHordeAt = this.time;
      if (this.lastHordeAt !== null && this.time >= this.lastHordeAt + v.holdSeconds - 1e-9) {
        const alive = this.players.filter((p) => this.status.get(p.id) === 'playing');
        if (alive.length) this.end({ winnerTeam: alive[0]!.team, winners: alive.map((p) => p.id), reason: 'You held the line — the last horde is spent.', at: this.time });
      }
    }
  }

  snapshot(): { time: number; status: Record<string, PlayerStatus>; result: MatchResult | null; lastHordeAt: number | null } {
    return { time: this.time, status: Object.fromEntries([...this.status].sort()), result: this.ended, lastHordeAt: this.lastHordeAt };
  }

  private check(): void {
    if (this.ended) return;
    // Defeat: no Command Center left.
    for (const p of this.players) {
      if (this.status.get(p.id) === 'defeated') continue;
      if (this.status.get(p.id) !== 'playing') continue;
      if (this.buildings.ownedBy(p.id).some((b) => b.type === 'command_center')) continue;
      this.status.set(p.id, 'defeated');
      this.emit({ type: 'defeated', playerId: p.id, reason: 'Command Center destroyed' });
    }
    if (this.mode.victory.kind === 'raid') {
      const [attacker, defender] = this.players;
      if (defender && this.status.get(defender.id) === 'defeated' && attacker && this.status.get(attacker.id) === 'playing') {
        this.end({ winnerTeam: attacker.team, winners: [attacker.id], reason: "The defender's Command Center is destroyed.", at: this.time });
      } else if (attacker && this.status.get(attacker.id) === 'defeated' && defender) {
        this.end({ winnerTeam: defender.team, winners: [defender.id], reason: "The attacker's Command Center is destroyed.", at: this.time });
      }
      return;
    }
    const alive = this.players.filter((p) => this.status.get(p.id) === 'playing');
    const teams = new Set(alive.map((p) => p.team));
    if (this.players.length > 1 && teams.size <= 1) {
      const team = alive[0]?.team ?? null;
      this.end({ winnerTeam: team, winners: alive.map((p) => p.id), reason: team === null ? 'Every colony has fallen.' : 'The last colony standing.', at: this.time });
    } else if (this.players.length === 1 && alive.length === 0) {
      this.end({ winnerTeam: null, winners: [], reason: 'Your colony has fallen.', at: this.time });
    }
  }

  private end(r: MatchResult): void {
    this.ended = r;
    this.emit({ type: 'ended', result: r });
  }

  private emit(e: MatchEvent): void {
    for (const l of this.listeners) l(e);
  }
}
